import React from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';

import { DragSource, DropTarget, DRAG_TYPES } from '@techchange/dnd';
import { messages } from '../../../../../../_shared/i18n/messages';

import { FIELD_TYPES } from '../../../../../constants';
import { useFormEditorContext } from '../../../contexts/FormEditorContext';
import { FIELD_EDITOR_FIELDS } from '../../../hooks/useFormEditorData';
import {
	formFieldDataShape,
	fieldOptionsInputValidationShape,
} from '../../../utils/propTypes';

import FormEditorFieldOption from './FormEditorFieldOption';

import './FormEditorFieldOptions.scss';

const propTypes = {

	/**
	 * The form field.
	 */
	field: formFieldDataShape.isRequired,

	/**
	 * The input validation for the list of form field options.
	 */
	optionsValidation: fieldOptionsInputValidationShape,

	/**
	 * Flag indicating if conditional navigation options for the form field
	 * options should be visible.
	 */
	showNavOptions: PropTypes.bool,

};

const FormEditorFieldOptions = (props) => {

	const {
		field,
		optionsValidation,
		showNavOptions,
	} = props;

	const intl = useIntl();

	const {
		defaultLanguage,
		selectedLanguage,
		isEditingDefaultLanguage,
		actions,
	} = useFormEditorContext();

	const fieldType = field[FIELD_EDITOR_FIELDS.TYPE];
	const options = field[FIELD_EDITOR_FIELDS.OPTIONS] || [];

	// Get the form field content data for the selected language
	const activeTranslation =
		field[FIELD_EDITOR_FIELDS.CONTENTS].find(
			(content) => content.language === selectedLanguage
		);

	// Get the form field content data for the default language
	const defaultTranslation =
		field[FIELD_EDITOR_FIELDS.CONTENTS].find(
			(content) => content.language === defaultLanguage
		);

	// Build the placeholder text override for the select field placeholder value
	const placeholderTextOverrideForSelect =
		isEditingDefaultLanguage ? null : defaultTranslation?.[FIELD_EDITOR_FIELDS.PLACEHOLDER];

	return (
		<div className="tc-form-editor-field-options">
			{fieldType === FIELD_TYPES.SELECT &&
				<div className="tc-form-editor-field-option tc-form-editor-field-option-placeholder-text">
					<input
						className="tc-material-input"
						type="text"
						value={activeTranslation?.[FIELD_EDITOR_FIELDS.PLACEHOLDER] || ''}
						placeholder={
							placeholderTextOverrideForSelect ||
							intl.formatMessage(messages.TC_COMPONENTS_FORM_EDITOR_INPUT_PLACEHOLDER_TEXT_DUMMY_SELECT)
						}
						onChange={(event) => actions.fields.update(
							field.id,
							FIELD_EDITOR_FIELDS.PLACEHOLDER,
							event.target.value,
						)} />
				</div>
			}
			{options.map((option, index) => (
				<DropTarget
					key={option.id}
					direction="vertical"
					type={DRAG_TYPES.FORM_FIELD_OPTION}
					item={option}
					prevItem={options[index - 1]}
					nextItem={options[index + 1]}>
					<DragSource
						type={DRAG_TYPES.FORM_FIELD_OPTION}
						item={option}
						onMove={(optionID, prev, next) =>
							actions.options.move(field.id, optionID, prev, next)
						}>
						<FormEditorFieldOption
							fieldId={field.id}
							type={fieldType}
							option={option}
							optionValidation={optionsValidation?.data?.[option.id]}
							index={index}
							showDeleteButton={options.length > 2}
							showNavOptions={showNavOptions} />
					</DragSource>
				</DropTarget>
			))}
			{isEditingDefaultLanguage &&
				<FormEditorFieldOption
					fieldId={field.id}
					type={fieldType}
					option={null}
					index={options.length}
					placeholder />
			}
		</div>
	);

};

FormEditorFieldOptions.propTypes = propTypes;

export default FormEditorFieldOptions;
